import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiChevronLeft, FiChevronRight, FiX, FiZoomIn, FiPause, FiPlay } = FiIcons;

const ImageGallery = () => {
  const images = [
    "https://lima-service.de/Grafiken/Umbau/13.jpg",
    "https://lima-service.de/Grafiken/Umbau/14.jpg",
    "https://lima-service.de/Grafiken/Umbau/15.jpg",
    "https://lima-service.de/Grafiken/Umbau/16.jpg",
    "https://lima-service.de/Grafiken/Umbau/17.jpg",
    "https://lima-service.de/Grafiken/Umbau/18.jpg",
    "https://lima-service.de/Grafiken/Umbau/19.jpg",
    "https://lima-service.de/Grafiken/Umbau/21.jpeg",
    "https://lima-service.de/Grafiken/Umbau/22.jpeg",
    "https://lima-service.de/Grafiken/Umbau/23.jpeg",
    "https://lima-service.de/Grafiken/Umbau/24.jpg",
    "https://lima-service.de/Grafiken/Umbau/25.jpg"
  ];

  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [itemsPerView, setItemsPerView] = useState(3);
  const timerRef = useRef(null);

  // Responsive items per view
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 640) setItemsPerView(1);
      else if (window.innerWidth < 1024) setItemsPerView(2);
      else setItemsPerView(3);
    };
    
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Auto-scroll logic
  useEffect(() => {
    if (!isPaused && !lightboxOpen) {
      timerRef.current = setInterval(() => {
        nextSlide();
      }, 3000); // Change slide every 3 seconds
    }
    return () => clearInterval(timerRef.current);
  }, [isPaused, lightboxOpen, currentIndex, itemsPerView]); // Dependencies for recreating timer

  const nextSlide = () => {
    setCurrentIndex((prev) => {
        // Loop back to start smoothly could be complex with partial views, 
        // simple wrap around:
        const maxIndex = images.length - itemsPerView;
        return prev >= maxIndex ? 0 : prev + 1;
    });
  };

  const prevSlide = () => {
    setCurrentIndex((prev) => {
        const maxIndex = images.length - itemsPerView;
        return prev <= 0 ? maxIndex : prev - 1;
    });
  };

  const openLightbox = (index) => {
    setLightboxIndex(index);
    setLightboxOpen(true);
    setIsPaused(true);
  };

  const closeLightbox = () => {
    setLightboxOpen(false);
    setIsPaused(false);
  };

  const nextLightbox = (e) => {
    e.stopPropagation();
    setLightboxIndex((prev) => (prev + 1) % images.length);
  };

  const prevLightbox = (e) => {
    e.stopPropagation();
    setLightboxIndex((prev) => (prev - 1 + images.length) % images.length);
  };

  // Calculate generic translation percentage based on index
  // Each item takes up 100% / itemsPerView width
  // Gap handling is simplified by using padding in inner container
  
  return (
    <section className="py-20 bg-gray-900 border-t border-gray-800">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div 
            initial={{ y: 50, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true }}
            className="text-center mb-12"
        >
          <h2 className="text-4xl sm:text-5xl font-bold text-white mb-6">
            Unsere <span className="gradient-text">Arbeit</span>
          </h2>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            Einblicke in unsere Projekte und Umbauten
          </p>
        </motion.div>

        {/* Carousel Container */}
        <div 
          className="relative group"
          onMouseEnter={() => setIsPaused(true)}
          onMouseLeave={() => !lightboxOpen && setIsPaused(false)}
        >
          {/* Controls */}
          <button 
            onClick={prevSlide}
            className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-1/2 z-20 w-12 h-12 bg-gray-800/80 hover:bg-orange-500 text-white rounded-full flex items-center justify-center backdrop-blur-sm border border-gray-600 transition-all shadow-lg opacity-0 group-hover:opacity-100 disabled:opacity-0"
            style={{ display: 'flex' }} // Always keep layout
          >
            <SafeIcon icon={FiChevronLeft} className="w-6 h-6" />
          </button>
          
          <button 
            onClick={nextSlide}
            className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-1/2 z-20 w-12 h-12 bg-gray-800/80 hover:bg-orange-500 text-white rounded-full flex items-center justify-center backdrop-blur-sm border border-gray-600 transition-all shadow-lg opacity-0 group-hover:opacity-100"
          >
            <SafeIcon icon={FiChevronRight} className="w-6 h-6" />
          </button>

          {/* Pause/Play Status Indicator (Optional, subtle) */}
          <div className="absolute top-4 right-4 z-20 px-3 py-1 bg-black/50 rounded-full text-xs text-white flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
             <SafeIcon icon={isPaused ? FiPause : FiPlay} className="w-3 h-3" />
             <span>{isPaused ? 'Pause' : 'Auto'}</span>
          </div>

          {/* Slider Window */}
          <div className="overflow-hidden rounded-2xl">
            <motion.div 
              className="flex"
              animate={{ x: `-${currentIndex * (100 / itemsPerView)}%` }}
              transition={{ type: "spring", stiffness: 300, damping: 30 }}
            >
              {images.map((img, index) => (
                <div 
                  key={index} 
                  className="flex-shrink-0 px-2"
                  style={{ width: `${100 / itemsPerView}%` }}
                >
                  <motion.div 
                    className="relative aspect-w-4 aspect-h-3 rounded-xl overflow-hidden cursor-pointer group/item"
                    onClick={() => openLightbox(index)}
                    whileHover={{ scale: 1.02 }}
                  >
                    <img 
                      src={img} 
                      alt={`Umbau Projekt ${index + 1}`} 
                      className="w-full h-full object-cover transition-transform duration-700 group-hover/item:scale-110"
                      loading="lazy"
                    />
                    <div className="absolute inset-0 bg-black/0 group-hover/item:bg-black/30 transition-colors flex items-center justify-center opacity-0 group-hover/item:opacity-100">
                      <SafeIcon icon={FiZoomIn} className="w-10 h-10 text-white drop-shadow-lg" />
                    </div>
                  </motion.div>
                </div>
              ))}
            </motion.div>
          </div>
          
          {/* Dots Navigation */}
          <div className="flex justify-center mt-6 gap-2 flex-wrap">
            {Array.from({ length: images.length - itemsPerView + 1 }).map((_, idx) => (
                // Only show a limited number of dots if too many images, but simpler logic here:
                // Show dot if it's a valid starting position
                <button
                    key={idx}
                    onClick={() => setCurrentIndex(idx)}
                    className={`h-2 rounded-full transition-all duration-300 ${
                        currentIndex === idx ? 'w-8 bg-orange-500' : 'w-2 bg-gray-600 hover:bg-gray-400'
                    }`}
                />
            ))}
          </div>
        </div>
      </div>

      {/* Lightbox Overlay */}
      <AnimatePresence>
        {lightboxOpen && (
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] bg-black/95 backdrop-blur-xl flex items-center justify-center p-4"
            onClick={closeLightbox}
          >
            {/* Close Button */}
            <button 
              className="absolute top-4 right-4 p-2 text-gray-400 hover:text-white hover:bg-white/10 rounded-full transition-colors z-50"
              onClick={closeLightbox}
            >
              <SafeIcon icon={FiX} className="w-8 h-8" />
            </button>

            {/* Nav Buttons */}
            <button 
              className="absolute left-4 top-1/2 -translate-y-1/2 p-3 text-white/50 hover:text-white hover:bg-white/10 rounded-full transition-colors z-50 hidden sm:block"
              onClick={prevLightbox}
            >
              <SafeIcon icon={FiChevronLeft} className="w-10 h-10" />
            </button>
            <button 
              className="absolute right-4 top-1/2 -translate-y-1/2 p-3 text-white/50 hover:text-white hover:bg-white/10 rounded-full transition-colors z-50 hidden sm:block"
              onClick={nextLightbox}
            >
              <SafeIcon icon={FiChevronRight} className="w-10 h-10" />
            </button>

            {/* Image Container */}
            <motion.div 
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="relative max-w-7xl max-h-[90vh] w-full h-full flex items-center justify-center"
              onClick={(e) => e.stopPropagation()}
            >
              <img 
                src={images[lightboxIndex]} 
                alt={`Detailansicht ${lightboxIndex + 1}`}
                className="max-w-full max-h-full object-contain rounded-lg shadow-2xl"
              />
              
              <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-black/60 px-4 py-2 rounded-full text-white text-sm backdrop-blur-md">
                Bild {lightboxIndex + 1} von {images.length}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};

export default ImageGallery;